import type { MutableRefObject } from "react";
import React, { startTransition } from "react";
import type { ClientError } from "@rosette/ts/src/business/game/error/ClientError.js";
import { Button } from "@rosette/web_components/src/ui/Button.js";
import { ErrorType } from "@rosette/ts/src/business/game/error/ErrorType.js";
import { useGameClientControls, useGameController } from "@/components/game/GameUIContext.js";
import { BotType } from "@rosette/ts/src/business/game/BotType.js";
import { Optional } from "@rosette/ts/src/util/Optional.js";
import { getMemburshipYearlyPriceAsMonthly } from "@rosette/ts/src/payments/stripe/products.js";
import { useFormatDate } from "@rosette/web_utils/src/time/useFormatDate.js";
import { MENU_BUTTON, MenuOptions } from "@/components/game/menu/MenuLayout.js";
import { PandaLooking } from "@/components/game/menu/game_end/PandaLooking.js";
import { Card } from "@rosette/web_components/src/ui/Card.js";


interface ErrorMenuProps {
    contentRef: MutableRefObject<HTMLDivElement | null>;
    error: ClientError;
}

export function ErrorMenu({ contentRef, error }: ErrorMenuProps) {
    const clientControls = useGameClientControls();
    const controller = useGameController();

    const pandaLimitData = error.getExceededPandaLimitData();
    const pandaAvailableAgainAt = useFormatDate(pandaLimitData?.available_again_at);

    if (pandaLimitData) {
        const currentSettings = controller.getGameSource().getKnownSetupSettings();
        const hardSettings = currentSettings?.withBotType(BotType.HARD);
        return (
            <MenuOptions contentRef={contentRef}>
                <div className="relative w-full mt-14">
                    <PandaLooking />
                    <Card>
                        <h1 className="mt-0 mb-4">
                            🕰️ Wait to play the Panda again
                        </h1>
                        <p>
                            You have played
                            {" "}
                            your daily free game against the Panda.
                            {" "}
                            You can play another free game
                            {" "}
                            {pandaAvailableAgainAt}
                            .
                            {" "}
                            Otherwise, join us as a membur and play unlimited games from just
                            {" "}
                            {getMemburshipYearlyPriceAsMonthly(false)}
                            !
                        </p>
                        <Button
                            tone="accent"
                            size="lg"
                            className="w-full"
                            href="/premium">

                            Join the Memburship!
                        </Button>

                        {hardSettings && (
                            <div className="px-3 pt-3 w-full">
                                <Button
                                    tone="primary"
                                    className="w-full"
                                    onClick={() => clientControls.startGame(hardSettings)}>

                                    Play Hard Bot
                                </Button>
                            </div>
                        )}
                    </Card>
                </div>
                <Button
                    className={MENU_BUTTON}
                    onClick={() => startTransition(() => {
                        const gameSource = controller.getGameSource();
                        gameSource.cancelRematch();
                        gameSource.error.set(Optional.empty());
                    })}>

                    Back
                </Button>
            </MenuOptions>
        );
    }
    return (
        <MenuOptions contentRef={contentRef}>
            <Card>
                <h1 className="mt-0 mb-4">
                    {error.getErrorType() === ErrorType.OUTDATED_CLIENT
                        ? "Please reload the page"
                        : "There was an error"}
                </h1>
                <p className="m-0 text-xl">
                    {error.message}
                </p>
                <div className="flex mt-6 w-full">
                    <Button
                        tone="accent"
                        size="lg"
                        className="w-full ml-auto mr-auto"
                        onClick={() => window.location.reload()}>

                        Reload Page
                    </Button>
                </div>
            </Card>

            <Button
                size="lg"
                className={MENU_BUTTON}
                onClick={() => clientControls.abortGame()}>
                Exit
            </Button>
        </MenuOptions>
    );
}
